import { ApolloLink, Observable, type FetchResult, type Operation } from '@apollo/client';
import type { GraphQLFormattedError } from 'graphql';
import { useEffect, useRef } from 'react';

/** 1s, 2s, 5s, then every 10s — the backend's suggested schedule. */
const RETRY_DELAYS_MS = [1000, 2000, 5000];
const RETRY_STEADY_MS = 10_000;

/** Errors that mean "stop": access is gone, or the session is (re-login handles it). */
const FINAL_CODES = new Set(['FORBIDDEN', 'UNAUTHENTICATED']);

const isFinal = (errors: readonly GraphQLFormattedError[] | undefined) =>
	!!errors?.some((e) => FINAL_CODES.has(String(e.extensions?.code ?? '')));

/* ── "subscriptions restored" signal ── */

type RestoredListener = (operationName: string | undefined) => void;
const restoredListeners = new Set<RestoredListener>();

/**
 * Fires each time a subscription is re-opened after its stream broke. Nothing
 * raised during the gap is replayed, so anything that must be correct —
 * a thread, a list, a count — refetches once on it.
 */
export function onSubscriptionRestored(listener: RestoredListener): () => void {
	restoredListeners.add(listener);
	return () => void restoredListeners.delete(listener);
}

/**
 * Hook form of `onSubscriptionRestored`. Pass the operation names you care about
 * (e.g. `['OnVetterJobUpdated']`), or none to react to any.
 */
export function useSubscriptionRestored(callback: () => void, operationNames?: string[]) {
	const latest = useRef(callback);
	latest.current = callback;
	const names = operationNames?.join('|') ?? '';
	useEffect(
		() =>
			onSubscriptionRestored((name) => {
				if (!names || (name && names.split('|').includes(name))) latest.current();
			}),
		[names]
	);
}

/**
 * Keeps each subscription alive across a backend restart.
 *
 * When a service restarts (every rebuild locally, every deploy or crash in
 * production) the gateway ends every subscription it held on it: an error
 * ("aborted", INTERNAL_SERVER_ERROR), then complete. The socket itself stays up,
 * so the transport's own reconnect never notices — only the operations died,
 * and nothing re-opened them. This link sits above either transport and does:
 *
 *  - error, then complete  → re-subscribe on a backoff, then signal "restored"
 *    so pages refetch what they may have missed;
 *  - FORBIDDEN / UNAUTHENTICATED → pass it on and stop: access is really gone
 *    (or the session is, and re-login takes over);
 *  - complete with no error before it → the server ended it on purpose (a
 *    thread whose viewer lost access) — pass it on, never retry.
 */
export class ResilientSubscriptionLink extends ApolloLink {
	public request(operation: Operation, forward?: (op: Operation) => Observable<FetchResult>) {
		if (!forward) return null;
		return new Observable<FetchResult>((observer) => {
			let closed = false;
			let attempt = 0;
			let broke = false;
			let timer: ReturnType<typeof setTimeout> | undefined;
			let current: { unsubscribe: () => void } | undefined;

			const retry = () => {
				if (closed) return;
				const delay = RETRY_DELAYS_MS[attempt] ?? RETRY_STEADY_MS;
				attempt += 1;
				timer = setTimeout(() => {
					if (closed) return;
					start();
					restoredListeners.forEach((l) => l(operation.operationName));
				}, delay);
			};

			const start = () => {
				broke = false;
				current = forward(operation).subscribe({
					next: (result) => {
						if (result.errors?.length && !isFinal(result.errors)) {
							// The stream broke (a restart): hold the error back — the
							// retry is the answer to it, not the page.
							broke = true;
							return;
						}
						if (!result.errors?.length) attempt = 0;
						observer.next(result);
					},
					error: (error) => {
						const errors = (error as { graphQLErrors?: GraphQLFormattedError[] })
							?.graphQLErrors;
						if (isFinal(errors)) {
							observer.error(error);
							return;
						}
						retry();
					},
					complete: () => {
						if (broke) {
							retry();
							return;
						}
						observer.complete();
					},
				});
			};

			start();

			return () => {
				closed = true;
				clearTimeout(timer);
				current?.unsubscribe();
			};
		});
	}
}
